import React from 'react';
import { Box, Button, Typography } from '@mui/material';

type ErrorBoundaryProps = { children: React.ReactNode };
type ErrorBoundaryState = { hasError: boolean };

// Without a boundary, any render error unmounts the whole tree and leaves a
// blank white page (see BL-TASK-1393/1394). console.error also forwards the
// crash to Datadog browser logs where enabled.
class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error(
      `Unhandled render error: ${error.message}`,
      error,
      errorInfo.componentStack,
    );
  }

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="h5">Something went wrong</Typography>
        <Typography color="text.secondary">
          An unexpected error occurred. Please reload the page — if this keeps
          happening, report it in Slack with the steps you took.
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Reload page
        </Button>
      </Box>
    );
  }
}

export default ErrorBoundary;
